import { useEffect, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { FlaskConical, Loader2, Timer, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Campaign } from './types'
import { useToggleTestMode } from './use-campaigns'

// Internal-only feature gate. Test Mode bypasses LinkedIn's normal pacing
// and is operationally risky (it's the same setting that contributed to
// the multi-day account ban during early testing). Owner directive
// 2026-07-14: Taha ONLY — not even Eli/TJ. The server enforces the same
// rule (campaigns worker 403s on the verified JWT subject), so this UI
// gate is presentation; the backend is the guarantee.
const TEST_MODE_ALLOWED_USER_IDS: ReadonlySet<string> = new Set([
  'user_3C4CzlgLjarwyXJkhGHHXMGA2SE', // taha.a.hashmi@gmail.com (Taha)
])

type TestModeButtonProps = {
  readonly campaign: Campaign
}

function formatCountdown(expiresAt: string): string {
  const remaining = new Date(expiresAt).getTime() - Date.now()
  if (remaining <= 0) return '0:00'
  const totalSec = Math.floor(remaining / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${min}:${sec.toString().padStart(2, '0')}`
}

/**
 * Test-mode toggle with explicit-consent dialog and a live countdown of the
 * 1-hour auto-expiry.
 *
 * The earlier version was a bare button — clicking it silently flipped the
 * dangerous "1-second-burst" pacing on. The new version makes the trade-offs
 * loud (in the dialog), surfaces the auto-expiry in the button label so the
 * operator always knows when it'll switch back, and re-renders every second
 * to count down.
 */
export function TestModeButton({ campaign }: TestModeButtonProps) {
  const { user } = useUser()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const toggleTestMode = useToggleTestMode()
  const isOn = Boolean(campaign.test_mode)
  const expiresAt = campaign.test_mode_expires_at ?? null
  const [, forceTick] = useState(0)

  // 1Hz tick to update the countdown label while test mode is on.
  // Hooks must run unconditionally — the gate render below decides whether
  // to mount any UI.
  useEffect(() => {
    if (!isOn || !expiresAt) return
    const interval = setInterval(() => forceTick((n) => n + 1), 1000)
    return () => clearInterval(interval)
  }, [isOn, expiresAt])

  // Internal-only feature gate — see TEST_MODE_ALLOWED_USER_IDS at top of
  // file. Beta users (anyone outside DCA staff) get no Test Mode UI at
  // all, since the toggle alters LinkedIn-pacing behavior in ways that
  // can ban an account if misused.
  const isAllowed = Boolean(user?.id && TEST_MODE_ALLOWED_USER_IDS.has(user.id))
  if (!isAllowed) return null

  function handleClick() {
    if (isOn) {
      // Turning off requires no confirmation — that's the safe direction.
      toggleTestMode.mutate({ id: campaign.id, enabled: false })
    } else {
      setConfirmOpen(true)
    }
  }

  function confirmEnable() {
    toggleTestMode.mutate(
      { id: campaign.id, enabled: true },
      { onSuccess: () => setConfirmOpen(false) },
    )
  }

  const countdown = isOn && expiresAt ? formatCountdown(expiresAt) : null

  return (
    <>
      <Button
        variant={isOn ? 'default' : 'outline'}
        size="sm"
        className={
          isOn
            ? 'bg-amber-500 hover:bg-amber-400 text-amber-950 gap-1.5'
            : 'gap-1.5'
        }
        onClick={handleClick}
        disabled={toggleTestMode.isPending}
      >
        {toggleTestMode.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isOn ? (
          <Timer className="h-4 w-4" />
        ) : (
          <FlaskConical className="h-4 w-4" />
        )}
        {toggleTestMode.isPending
          ? 'Switching…'
          : isOn
            ? `Test Mode · ${countdown ?? 'on'}`
            : 'Test Mode'}
      </Button>

      <Dialog open={confirmOpen} onOpenChange={(o) => !toggleTestMode.isPending && setConfirmOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-amber-500" />
              <DialogTitle>Enable Test Mode?</DialogTitle>
            </div>
            <DialogDescription className="pt-2 text-foreground/90">
              Test Mode lets you iterate on this campaign without waiting on
              normal production timing. It auto-disables after <strong>1 hour</strong> so
              an unattended account can&apos;t accumulate damage.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-emerald-500/25 bg-emerald-500/10 p-3 space-y-1.5">
              <p className="font-semibold text-emerald-300">What changes</p>
              <ul className="list-disc pl-5 space-y-1 text-[var(--color-silver-bright)]">
                <li>Activity-window check is bypassed (sends at any hour)</li>
                <li>Per-step <code className="text-xs">delay_days</code> is bypassed (sequence chains immediately)</li>
                <li>Pacing tightens to <strong>30 seconds</strong> between actions (vs 15–300s in normal mode)</li>
              </ul>
            </div>

            <div className="rounded-md border border-[rgba(28,63,255,0.25)] bg-[rgba(28,63,255,0.08)] p-3 space-y-1.5">
              <p className="font-semibold text-[var(--color-cobalt-bright)]">What stays on (your safety nets)</p>
              <ul className="list-disc pl-5 space-y-1 text-[var(--color-silver-bright)]">
                <li>Per-lead retry-after — failed leads still get parked</li>
                <li>Daily limit cap — your plan&apos;s max sends/day still applies</li>
                <li>Hard cap of <strong>3 actions per orchestrator tick</strong> while Test Mode is on</li>
                <li>Warmup pause — banned/paused accounts still skip outreach</li>
              </ul>
            </div>

            <div className="flex items-start gap-2 rounded-md border border-amber-500/25 bg-amber-500/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
              <p className="text-amber-200">
                <strong>Auto-disables in 1 hour.</strong> The button label will show
                a countdown. You can also turn it off manually any time.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={toggleTestMode.isPending}
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-400 text-amber-950"
              onClick={confirmEnable}
              disabled={toggleTestMode.isPending}
            >
              {toggleTestMode.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enabling…</>
              ) : (
                <><FlaskConical className="h-4 w-4 mr-1" /> Enable for 1 hour</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
